import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { StudentTestAssignmentService } from '../../../../core/services/assignments/student-test-assignment.service';
import { TestAssignment } from '../../../../core/interfaces/test-assignment';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
type FilterTab = 'all' | 'exam' | 'practice' | 'evaluation';

@Component({
  selector: 'app-homework',
  standalone: true,
  imports: [CommonModule, TranslateModule, ErrorStateComponent, LocalizedDatePipe],
  templateUrl: './homework.component.html',
  styleUrl: './homework.component.css',
})
export class HomeworkComponent implements OnInit {
  private readonly service = inject(StudentTestAssignmentService);
  private readonly router = inject(Router);

  readonly assignments = signal<TestAssignment[]>([]);
  readonly loading = signal(true);
  readonly errored = signal(false);
  readonly activeTab = signal<FilterTab>('all');
  readonly query = signal('');

  readonly filtered = computed(() => {
    const tab = this.activeTab();
    const q = this.query().trim().toLowerCase();
    return this.assignments()
      .filter((a) => tab === 'all' || a.testMode === tab)
      .filter((a) => {
        if (!q) return true;
        return [a.testTitle, a.subjectName, a.instructions, a.professorFullName]
          .filter(Boolean).join(' ').toLowerCase().includes(q);
      });
  });

  readonly counts = computed(() => {
    const all = this.assignments();
    return {
      all: all.length,
      exam: all.filter((a) => a.testMode === 'exam').length,
      practice: all.filter((a) => a.testMode === 'practice').length,
      evaluation: all.filter((a) => a.testMode === 'evaluation').length,
    };
  });

  ngOnInit(): void { this.load(); }

  setTab(tab: FilterTab): void { this.activeTab.set(tab); }
  onQuery(v: string): void { this.query.set(v); }

  /**
   * Ouvre la passation du devoir. `/student/tests/:id` n'existait pas : le
   * bouton menait nulle part. La passation est la même que pour les autres
   * tests, et le serveur l'ouvre au pack qui inclut les devoirs.
   */
  startTest(a: TestAssignment): void {
    this.router.navigate(['/student/test/take', a.testId]);
  }

  isDueSoon(a: TestAssignment): boolean {
    if (!a.dueDate) return false;
    const diff = new Date(a.dueDate).getTime() - Date.now();
    return diff > 0 && diff < 1000 * 60 * 60 * 48; // < 48 h
  }

  isOverdue(a: TestAssignment): boolean {
    if (!a.dueDate) return false;
    return new Date(a.dueDate).getTime() < Date.now();
  }

  modeIcon(mode: string | null | undefined): string {
    switch (mode) {
      case 'exam':       return 'fa-solid fa-file-pen';
      case 'practice':   return 'fa-solid fa-dumbbell';
      case 'evaluation': return 'fa-solid fa-chart-simple';
      default:           return 'fa-solid fa-clipboard-question';
    }
  }

  targetIcon(a: TestAssignment): string {
    if (a.targetType === 'group')   return 'fa-solid fa-users';
    if (a.targetType === 'subject') return 'fa-solid fa-book';
    return 'fa-solid fa-user-graduate';
  }

  targetLabel(a: TestAssignment): string {
    if (a.targetType === 'group')   return a.groupName   ?? `Groupe #${a.groupId}`;
    if (a.targetType === 'subject') return a.subjectName ?? `Matière #${a.subjectId}`;
    return a.studentFullName ?? 'Personnel';
  }

  /** Rechargement manuel, déclenché par <app-error-state>. */
  reload(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.service.myAssignments().subscribe({
      next: (res) => {
        this.assignments.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.assignments.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }
}

